'use strict';

// This XHR-to-fetch adapter follows OPT's PersistenceXMLHttpRequest approach, adapted for VBRT's
// emulated service-worker mode rather than as a full OPT XMLHttpRequest replacement.
define('vbsw/private/emulatedXMLHttpRequest',['vbc/private/log'], (Log) => {
  const logger = Log.getLogger('/vbsw/private/emulatedXMLHttpRequest');

  const UNSENT = 0;
  const OPENED = 1;
  const HEADERS_RECEIVED = 2;
  const LOADING = 3;
  const DONE = 4;

  const BODYLESS_METHODS = ['GET', 'HEAD'];

  function createEvent(type, target) {
    return {
      type,
      target,
      currentTarget: target,
      bubbles: false,
      cancelable: false,
      defaultPrevented: false,
      preventDefault() {
        this.defaultPrevented = true;
      },
      stopPropagation() {
        // This is not a DOM event.
      },
    };
  }

  function ignoreError(error) {
    return !!error;
  }

  function isFileRequest(url) {
    const normalizedUrl = String(url || '').toLowerCase();
    if (normalizedUrl.startsWith('file:') || normalizedUrl.startsWith('cdvfile:')) {
      return true;
    }
    if (normalizedUrl.startsWith('http:') || normalizedUrl.startsWith('https:')) {
      return false;
    }

    try {
      const absoluteUrl = new URL(url, window.location.href);
      return absoluteUrl.protocol === 'file:' || absoluteUrl.protocol === 'cdvfile:';
    } catch (error) {
      ignoreError(error);
      return false;
    }
  }

  function normalizeResponseHeaders(headers) {
    const responseHeaders = {};
    if (headers && headers.forEach) {
      headers.forEach((value, name) => {
        responseHeaders[name] = value;
      });
    }
    return responseHeaders;
  }

  function EmulatedXMLHttpRequestUpload() {
    this._eventListeners = {};
    this.onabort = null;
    this.onerror = null;
    this.onload = null;
    this.onloadend = null;
    this.onloadstart = null;
    this.onprogress = null;
    this.ontimeout = null;
  }

  EmulatedXMLHttpRequestUpload.prototype.addEventListener = function addEventListener(eventType, listener) {
    this._eventListeners[eventType] = this._eventListeners[eventType] || [];
    this._eventListeners[eventType].push(listener);
  };

  EmulatedXMLHttpRequestUpload.prototype.removeEventListener = function removeEventListener(eventType, listener) {
    const listeners = this._eventListeners[eventType] || [];
    const index = listeners.indexOf(listener);
    if (index >= 0) {
      listeners.splice(index, 1);
    }
  };

  function createEmulatedXMLHttpRequest(browserXMLHttpRequest) {
    const BrowserXMLHttpRequest = browserXMLHttpRequest;

    class EmulatedXMLHttpRequest {
      constructor() {
        this._abortController = typeof AbortController === 'undefined' ? null : new AbortController();
        this._eventListeners = {};
        this._method = null;
        this._passthroughXHR = null;
        this._readyState = UNSENT;
        this._requestAborted = false;
        this._requestActive = false;
        this._requestHeaders = {};
        /** @type {*} */
        this._response = '';
        this._responseHeaders = {};
        this._responseText = '';
        this._responseURL = '';
        this._responseXML = null;
        this._status = 0;
        this._statusText = '';
        this._timeoutId = null;
        this._url = null;
        this._username = null;
        this._password = null;

        this.onabort = null;
        this.onerror = null;
        this.onload = null;
        this.onloadend = null;
        this.onloadstart = null;
        this.onprogress = null;
        this.onreadystatechange = null;
        this.ontimeout = null;
        this.responseType = '';
        this.timeout = 0;
        this.upload = new EmulatedXMLHttpRequestUpload();
        this.withCredentials = false;
      }

      get readyState() {
        return this._readyState;
      }

      get response() {
        return this._response;
      }

      get responseText() {
        return this._responseText;
      }

      get responseURL() {
        return this._responseURL;
      }

      get responseXML() {
        return this._responseXML;
      }

      get status() {
        return this._status;
      }

      get statusText() {
        return this._statusText;
      }

      open(method, url, async, username, password) {
        const isAsync = async !== false;
        logger.info(`Emulated XMLHttpRequest: open() for method: ${method}, url: ${url}`);

        if (!isAsync) {
          throw new Error("InvalidAccessError: Failed to execute 'open' on 'XMLHttpRequest': "
            + 'Synchronous requests are disabled on the XHR adapter');
        }

        this._method = method;
        this._url = url;
        this._username = username;
        this._password = password;
        this._requestHeaders = {};
        this._requestAborted = false;
        this._requestActive = false;
        this._response = '';
        this._responseHeaders = {};
        this._responseText = '';
        this._responseURL = '';
        this._responseXML = null;
        this._status = 0;
        this._statusText = '';
        this._abortController = typeof AbortController === 'undefined' ? null : new AbortController();

        if (isFileRequest(url)) {
          this._passthroughXHR = new BrowserXMLHttpRequest();
          this._wirePassthroughHandlers();
          this._passthroughXHR.open(method, url, isAsync, username, password);
        } else {
          this._passthroughXHR = null;
        }

        this._readyStateChange(OPENED);
      }

      setRequestHeader(header, value) {
        this._verifyState();
        const oldValue = this._requestHeaders[header];
        this._requestHeaders[header] = oldValue ? `${oldValue},${value}` : value;
      }

      send(data) {
        logger.info('Emulated XMLHttpRequest: send()');
        if (this._passthroughXHR) {
          this._passthroughXHR.responseType = this.responseType;
          this._passthroughXHR.timeout = this.timeout;
          this._passthroughXHR.withCredentials = this.withCredentials;
          this._passthroughXHR.send(data);
          return;
        }

        this._verifyState();
        this._requestAborted = false;
        this._requestActive = true;
        this._readyStateChange(OPENED);
        this.dispatchEvent(createEvent('loadstart', this));

        const request = new Request(this._url, this._getRequestInit(data));
        const options = this._abortController ? { signal: this._abortController.signal } : undefined;

        // Call global fetch so XHR follows the same interceptor chain as native fetch, including VBRT's
        // emulated service-worker plugins and any OPT fetch wrapper that may be installed later.
        let timedOut = false;
        if (this.timeout && typeof this.timeout === 'number') {
          this._timeoutId = setTimeout(() => {
            timedOut = true;
            if (this._abortController) {
              this._abortController.abort();
            }
            this._status = 0;
            this._statusText = '';
            this._readyStateChange(DONE, 'timeout');
          }, this.timeout);
        }

        fetch(request, options)
          .then((response) => this._processResponse(request, response))
          .catch((error) => {
            if (timedOut || this._requestAborted) {
              return;
            }

            this._clearTimeout();
            this._status = 0;
            this._statusText = '';
            this._readyStateChange(DONE, error && error.name === 'AbortError' ? 'abort' : 'error');
          });
      }

      abort() {
        logger.info('Emulated XMLHttpRequest: abort()');
        if (this._passthroughXHR) {
          this._passthroughXHR.abort();
          return;
        }

        if (!this._requestActive) {
          this._readyState = UNSENT;
          this._status = 0;
          this._statusText = '';
          return;
        }

        this._requestAborted = true;
        if (this._abortController) {
          this._abortController.abort();
        }
        this._clearTimeout();
        this._requestActive = false;
        this._status = 0;
        this._statusText = '';
        this._readyStateChange(UNSENT);
        this.dispatchEvent(createEvent('abort', this));
        this.dispatchEvent(createEvent('loadend', this));
      }

      getResponseHeader(header) {
        if (this._readyState < HEADERS_RECEIVED || !header) {
          return null;
        }

        const normalizedHeader = header.toLowerCase();
        const responseHeader = Object.keys(this._responseHeaders)
          .find((name) => name.toLowerCase() === normalizedHeader);
        return responseHeader ? this._responseHeaders[responseHeader] : null;
      }

      getAllResponseHeaders() {
        if (this._readyState < HEADERS_RECEIVED) {
          return '';
        }

        return Object.keys(this._responseHeaders)
          .map((header) => `${header}: ${this._responseHeaders[header]}`)
          .join('\r\n');
      }

      overrideMimeType(mimeType) {
        if (typeof mimeType === 'string') {
          this._forceMimeType = mimeType.toLowerCase();
        }
      }

      addEventListener(eventType, listener) {
        this._eventListeners[eventType] = this._eventListeners[eventType] || [];
        this._eventListeners[eventType].push(listener);
      }

      removeEventListener(eventType, listener) {
        const listeners = this._eventListeners[eventType] || [];
        const index = listeners.indexOf(listener);
        if (index >= 0) {
          listeners.splice(index, 1);
        }
      }

      dispatchEvent(event) {
        const listeners = this._eventListeners[event.type] || [];
        listeners.slice().forEach((listener) => {
          if (typeof listener === 'function') {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === 'function') {
            listener.handleEvent(event);
          }
        });

        const handler = this[`on${event.type}`];
        if (typeof handler === 'function') {
          handler.call(this, event);
        }

        return !event.defaultPrevented;
      }

      _wirePassthroughHandlers() {
        const events = [
          'abort',
          'error',
          'load',
          'loadend',
          'loadstart',
          'progress',
          'readystatechange',
          'timeout',
        ];
        events.forEach((eventType) => {
          this._passthroughXHR[`on${eventType}`] = (event) => {
            this._copyPassthroughState();
            this.dispatchEvent(event || createEvent(eventType, this));
          };
        });
      }

      _copyPassthroughState() {
        this._readyState = this._passthroughXHR.readyState;
        this._status = this._passthroughXHR.status;
        this._statusText = this._passthroughXHR.statusText;
        this._response = this._passthroughXHR.response;
        this._responseText = this._passthroughXHR.responseText;
        this._responseURL = this._passthroughXHR.responseURL;
        this._responseXML = this._passthroughXHR.responseXML;
      }

      _getRequestInit(data) {
        // Mirror OPT's XHR adapter shape so requests created through JET/jQuery ajax are equivalent to fetch
        // requests before the emulated service-worker fetch handler applies plugins such as auth.
        const headers = new Headers();
        Object.keys(this._requestHeaders).forEach((header) => {
          headers.append(header, this._requestHeaders[header]);
        });

        const method = this._method || 'GET';
        /** @type {RequestInit} */
        const requestInit = {
          method,
          headers,
          mode: 'cors',
          cache: 'default',
          credentials: this.withCredentials ? 'include' : 'same-origin',
        };

        if (!BODYLESS_METHODS.includes(method.toUpperCase())) {
          requestInit.body = data;
        }

        return requestInit;
      }

      _processResponse(request, response) {
        if (this._requestAborted) {
          return;
        }

        this._clearTimeout();
        this._responseHeaders = normalizeResponseHeaders(response.headers);
        if (this._forceMimeType) {
          Object.keys(this._responseHeaders).forEach((header) => {
            if (header.toLowerCase() === 'content-type') {
              this._responseHeaders[header] = this._forceMimeType;
            }
          });
        }
        this._status = response.status;
        this._statusText = response.statusText;
        this._responseURL = response.url || request.url;
        this._readyStateChange(HEADERS_RECEIVED);
        this._readyStateChange(LOADING);

        this._readResponse(response)
          .then((body) => {
            if (this._requestAborted) {
              return;
            }

            this._setResponseBody(body);
            this._readyStateChange(DONE, 'load');
          })
          .catch((error) => {
            ignoreError(error);
            if (!this._requestAborted) {
              this._readyStateChange(DONE, 'error');
            }
          });
      }

      _readResponse(response) {
        switch (this.responseType) {
          case 'arraybuffer':
            return response.arrayBuffer();
          case 'blob':
            return response.blob();
          case 'json':
            return response.text();
          case 'document':
            return response.text();
          case '':
          case 'text':
          default:
            return response.text();
        }
      }

      _setResponseBody(body) {
        switch (this.responseType) {
          case 'arraybuffer':
          case 'blob':
            this._response = body;
            this._responseText = '';
            break;
          case 'json':
            this._responseText = body;
            try {
              this._response = body ? JSON.parse(body) : null;
            } catch (error) {
              ignoreError(error);
              this._response = null;
            }
            break;
          case 'document':
            this._responseText = body;
            this._response = body;
            this._responseXML = typeof DOMParser === 'undefined'
              ? null
              : new DOMParser().parseFromString(body, 'text/xml');
            break;
          case '':
          case 'text':
          default:
            this._response = body;
            this._responseText = body;
        }
      }

      _readyStateChange(state, terminalEventType) {
        this._readyState = state;
        this.dispatchEvent(createEvent('readystatechange', this));

        if (state === DONE && terminalEventType) {
          this._requestActive = false;
          this.dispatchEvent(createEvent(terminalEventType, this));
          this.dispatchEvent(createEvent('loadend', this));
        }
      }

      _verifyState() {
        if (this._readyState !== OPENED) {
          throw new Error('INVALID_STATE_ERR');
        }
      }

      _clearTimeout() {
        if (this._timeoutId) {
          clearTimeout(this._timeoutId);
          this._timeoutId = null;
        }
      }
    }

    Object.assign(EmulatedXMLHttpRequest, {
      UNSENT,
      OPENED,
      HEADERS_RECEIVED,
      LOADING,
      DONE,
    });
    Object.assign(EmulatedXMLHttpRequest.prototype, {
      UNSENT,
      OPENED,
      HEADERS_RECEIVED,
      LOADING,
      DONE,
    });

    return EmulatedXMLHttpRequest;
  }

  return createEmulatedXMLHttpRequest;
});

